import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { userFacingError } from '../utils/userFacingError';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { Button } from '../components/ui/Button';
import { Textarea } from '../components/ui/Textarea';
import PageHeader from '../components/ui/PageHeader';
import PageLoader from '../components/ui/PageLoader';
import UnsavedChangesNotice from '../components/ui/UnsavedChangesNotice';
import { DachthekenduettResultItem, DachthekenduettService } from '../services/dachthekenduettService';

const DachthekenduettPage: React.FC = () => {
  const { user, hasRole } = useAuth();
  const confirm = useConfirmation();
  const allowed = hasRole(['administrator', 'redakteur']);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [savedPrompt, setSavedPrompt] = useState('');
  const [transcript, setTranscript] = useState('');
  const [results, setResults] = useState<DachthekenduettResultItem[]>([]);
  const [exported, setExported] = useState('');
  const [reload, setReload] = useState(0);
  const localSignature = JSON.stringify([transcript, results]);
  const promptDirty = prompt !== savedPrompt;
  const localDirty = !!(transcript || results.length) && localSignature !== exported;
  useEffect(() => {
    if (!allowed) { setLoading(false); return; }
    let active = true;
    setLoading(true);
    DachthekenduettService.getConfig().then(config => {
      if (active) { setPrompt(config.prompt); setSavedPrompt(config.prompt); setLoadError(false); }
    }).catch(() => { if (active) setLoadError(true); }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [allowed, reload]);
  const save = async () => {
    if (!user || saving) return;
    const snapshot = prompt.trim();
    setSaving(true);
    try { await DachthekenduettService.saveConfig(snapshot, user.id); setPrompt(snapshot); setSavedPrompt(snapshot); toast.success('Schreibauftrag gespeichert.'); }
    catch (e) { toast.error(userFacingError(e, 'Speichern konnte nicht bestätigt werden. Deine Änderungen bleiben erhalten.')); }
    finally { setSaving(false); }
  };
  const process = async () => {
    if (processing) return;
    if (results.length && !(await confirm({ title: 'Neue Vorschläge erstellen?', description: 'Die bisherigen Ergebnisse werden ersetzt. Kopiere sie bei Bedarf zuerst.', confirmLabel: 'Neu generieren' }))) return;
    setProcessing(true);
    try { const items = await DachthekenduettService.processTranscript(transcript, prompt); setResults(items); toast.success('Vorschläge erstellt.'); }
    catch (e) { toast.error(userFacingError(e, 'Die Vorschläge konnten nicht erstellt werden. Bitte versuche es erneut.')); }
    finally { setProcessing(false); }
  };
  const copy = async (value: string, all = false) => {
    try { await navigator.clipboard.writeText(value); if (all) setExported(localSignature); toast.success('In Zwischenablage kopiert.'); }
    catch { toast.error('Kopieren fehlgeschlagen. Bitte versuche es erneut.'); }
  };
  if (!allowed) return <div className="card"><PageHeader title="Dachthekenduett" description="Du hast keine Berechtigung für dieses Werkzeug." /></div>;
  if (loading) return <PageLoader />;
  if (loadError) return <div className="card" role="alert"><PageHeader title="Dachthekenduett" description="Die Einstellungen konnten nicht geladen werden." /><Button variant="outline" onClick={() => setReload(n => n + 1)}>Erneut versuchen</Button></div>;
  return <div className="space-y-6">
    <PageHeader title="Dachthekenduett" description="Erstelle aus dem Transkript einer Sendung Einleitung, Kapitelmarken, Tags, Titelvorschläge und Zitate." />
    <UnsavedChangesNotice dirty={promptDirty || localDirty} busy={saving || processing} localOnly={!promptDirty} />
    <section className="card space-y-4" aria-labelledby="transcript-heading">
      <h2 id="transcript-heading" className="text-lg text-gray-900">1. Transkript vorbereiten</h2>
      <Textarea label="Transkript" value={transcript} onChange={e => setTranscript(e.target.value)} rows={12} placeholder="Transkript der Sendung einfügen …" disabled={processing} />
      <div className="action-row"><Button onClick={process} loading={processing} disabled={!transcript.trim() || !prompt.trim() || saving}>Vorschläge generieren</Button></div>
    </section>
    <section className="card space-y-4" aria-labelledby="results-heading">
      <div className="flex flex-wrap items-center justify-between gap-3"><h2 id="results-heading" className="text-lg text-gray-900">2. Ergebnisse übernehmen</h2>
        <Button variant="outline" disabled={!results.length} onClick={() => copy(results.map(item => `${item.label}\n${item.value}`).join('\n\n'), true)}>Alle Ergebnisse kopieren</Button></div>
      {!results.length ? <p className="text-sm text-gray-600">Füge ein Transkript ein und starte die Erstellung. Hier erscheinen anschließend deine Vorschläge.</p> : results.map((item, index) => <div key={index} className="rounded-lg border border-gray-200 p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3"><h3 className="font-medium">{item.label}</h3><Button variant="outline" size="sm" onClick={() => copy(item.value)}>Kopieren</Button></div>
        <Textarea aria-label={item.label} value={item.value} readOnly rows={8} />
      </div>)}
    </section>
    <details className="card"><summary className="cursor-pointer text-lg font-semibold">Schreibauftrag — Einstellungen</summary>
      <div className="mt-4 space-y-4"><p className="text-sm text-gray-600">Der aktuelle Schreibauftrag gilt für die Erstellung oben. Mit „Änderungen speichern“ verwendest du ihn auch bei künftigen Aufträgen.</p>
        <Textarea label="Schreibauftrag" value={prompt} onChange={e => setPrompt(e.target.value)} rows={8} disabled={saving || processing} />
        <div className="flex flex-wrap items-center justify-between gap-3"><p role="status" className="text-sm text-gray-600">{saving ? 'Wird gespeichert …' : promptDirty ? 'Ungespeicherte Änderungen' : 'Schreibauftrag gespeichert'}</p>
          <Button variant="outline" onClick={save} loading={saving} disabled={!promptDirty || !prompt.trim() || processing}>Änderungen speichern</Button></div>
      </div>
    </details>
  </div>;
};
export default DachthekenduettPage;
